// ============================================================
// MANIC MEETER — Locale runtime (detection, persistence, switching)
// ============================================================

export type Locale = "de" | "en";

export const LOCALES: readonly Locale[] = ["de", "en"] as const;

const STORAGE_KEY = "manic-meeter-lang";

function isLocale(value: unknown): value is Locale {
  return value === "de" || value === "en";
}

/** Saved choice wins; otherwise the browser decides. German browsers get German. */
function detectLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    // localStorage can throw in private mode — fall through to detection
  }
  const nav = typeof navigator !== "undefined" ? navigator.language ?? "" : "";
  return nav.toLowerCase().startsWith("de") ? "de" : "en";
}

let current: Locale = detectLocale();

type LocaleListener = (locale: Locale) => void;
const listeners = new Set<LocaleListener>();

export function getLocale(): Locale {
  return current;
}

/** The language the player did *not* choose — the tongue of a certain colleague. */
export function otherLocale(locale: Locale = current): Locale {
  return locale === "de" ? "en" : "de";
}

export function setLocale(locale: Locale): void {
  if (locale === current) return;
  current = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Not persisting is survivable; the session still switches.
  }
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
  for (const listener of listeners) listener(locale);
}

/** Subscribe to switches. Returns an unsubscribe function. */
export function onLocaleChange(listener: LocaleListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test seam: forget subscribers and re-read the environment. */
export function resetLocaleForTests(): void {
  listeners.clear();
  current = detectLocale();
}
